import { useMemo } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { StaffBookingSummaryResponse } from "@repo/api-client";
import { useVenue } from "../../shell/VenueContext";
import type { ZoneResponse } from "../../features/floor-plan/floorApi";
import { bookingClock, guestName, isNum } from "../format";
import { layoutLane } from "../occupancy";
import { flattenPages, useBookingsList, useFloorPlan } from "../queries";
import type { BookingFilter } from "../searchParams";
import { toBookingStatus } from "../status";
import { statusTintClass } from "../statusStyles";
import { banner } from "../styles";

const UNASSIGNED = "__unassigned__";

/** Resolve a booking's lane: the zone whose id or name matches its `zonePreference`, else unassigned.
 *  (Table-level assignment is not yet on the staff query wire, so lanes are zone-scoped — an honest
 *  degradation until floor-plan-model surfaces the assignment.) */
function laneOf(b: StaffBookingSummaryResponse, zones: ZoneResponse[]): string {
  const pref = b.zonePreference?.trim().toLowerCase();
  if (!pref) return UNASSIGNED;
  const hit = zones.find((z) => z.id.toLowerCase() === pref || z.name.toLowerCase() === pref);
  return hit ? hit.id : UNASSIGNED;
}

/**
 * Timeline (day) view: one resource ROW per zone (per-table when floor-plan-model lands), bookings as
 * half-open occupied blocks on a venue-local time axis. Touching blocks never render as a conflict
 * (§B.4); a 01:00 slot whose service day is the previous date renders past the midnight gridline on
 * that day's row (cross-midnight anchor), never on the next day.
 */
export function TimelineView({
  day,
  filter,
  onSelect,
}: {
  day: string;
  filter: BookingFilter;
  onSelect: (b: StaffBookingSummaryResponse) => void;
}) {
  const { venueId, venue } = useVenue();
  const { t, i18n } = useTranslation();
  const dayFilter: BookingFilter = { ...filter, from: day, to: day };
  const query = useBookingsList(venueId, dayFilter);
  const plan = useFloorPlan(venueId);
  const rows = flattenPages(query.data);
  const zones = useMemo(() => plan.data?.zones ?? [], [plan.data]);

  const lanes = useMemo(() => {
    const byLane = new Map<string, StaffBookingSummaryResponse[]>();
    for (const z of zones) byLane.set(z.id, []);
    byLane.set(UNASSIGNED, []);
    for (const b of rows) {
      const lane = laneOf(b, zones);
      (byLane.get(lane) ?? byLane.get(UNASSIGNED))?.push(b);
    }
    return byLane;
  }, [rows, zones]);

  // Axis domain in minutes from the service-day midnight, derived from the day's blocks.
  const laid = useMemo(() => {
    const out = new Map<string, ReturnType<typeof layoutLane<StaffBookingSummaryResponse>>>();
    let min = 12 * 60;
    let max = 24 * 60;
    for (const [lane, items] of lanes) {
      const blocks = layoutLane(
        items,
        day,
        venue.timeZone,
        (b) => b.startSec ?? 0,
        (b) => b.durationSec ?? 0,
      );
      out.set(lane, blocks);
      for (const bl of blocks) {
        min = Math.min(min, bl.startMin - 30);
        max = Math.max(max, bl.endMin + 30);
      }
    }
    return { out, min, max: Math.max(max, min + 120) };
  }, [lanes, day, venue.timeZone]);

  if (query.isLoading || plan.isLoading) {
    return (
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("bookings.list.loading")}</p>
    );
  }

  const span = laid.max - laid.min || 1;
  const pct = (min: number) => `${((min - laid.min) / span) * 100}%`;
  const width = (a: number, b: number) => `${((b - a) / span) * 100}%`;
  const laneName = (lane: string) =>
    lane === UNASSIGNED
      ? t("bookings.timeline.unassigned")
      : (zones.find((z) => z.id === lane)?.name ?? lane);

  const hours: number[] = [];
  for (let h = Math.ceil(laid.min / 60); h * 60 <= laid.max; h++) hours.push(h);

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
      {rows.length === 0 ? (
        <p role="note" className={banner("info")}>
          {t("bookings.timeline.empty")}
        </p>
      ) : null}

      {/* Hour axis */}
      <div className={css({ display: "flex", pl: "32" })}>
        <div className={css({ position: "relative", flex: "1", h: "5" })}>
          {hours.map((h) => (
            <span
              key={h}
              className={css({
                position: "absolute",
                fontSize: "2xs",
                color: "fg.muted",
                transform: "translateX(-50%)",
              })}
              style={{ left: pct(h * 60) }}
            >
              {String(h % 24).padStart(2, "0")}h
            </span>
          ))}
        </div>
      </div>

      {[...laid.out.keys()].map((lane) => {
        const blocks = laid.out.get(lane) ?? [];
        if (lane === UNASSIGNED && blocks.length === 0) return null;
        return (
          <div key={lane} className={css({ display: "flex", alignItems: "stretch", gap: "2" })}>
            <div
              className={css({
                w: "32",
                flexShrink: "0",
                fontSize: "sm",
                fontWeight: "medium",
                display: "flex",
                alignItems: "center",
              })}
            >
              {laneName(lane)}
            </div>
            <div
              className={css({
                position: "relative",
                flex: "1",
                minH: "10",
                borderWidth: "1px",
                borderColor: "border.subtle",
                rounded: "sm",
                bg: "bg.subtle",
              })}
            >
              {blocks.map((bl) => {
                const status = toBookingStatus(bl.item.status);
                return (
                  <button
                    key={bl.item.bookingId}
                    type="button"
                    onClick={() => onSelect(bl.item)}
                    className={`${blockBase} ${status ? statusTintClass[status] : ""} ${
                      bl.conflict ? conflictRing : ""
                    }`}
                    style={{ left: pct(bl.startMin), width: width(bl.startMin, bl.endMin) }}
                    title={guestName(bl.item, t("bookings.noName"))}
                  >
                    <span className={css({ truncate: true })}>
                      {isNum(bl.item.startSec)
                        ? bookingClock(bl.item.startSec, venue.timeZone, i18n.language)
                        : ""}{" "}
                      · {guestName(bl.item, t("bookings.noName"))}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const blockBase = css({
  position: "absolute",
  top: "1",
  bottom: "1",
  minW: "8",
  px: "1.5",
  borderWidth: "1px",
  rounded: "sm",
  fontSize: "2xs",
  color: "fg.default",
  overflow: "hidden",
  textAlign: "left",
  display: "flex",
  alignItems: "center",
});

const conflictRing = css({
  outlineWidth: "2px",
  outlineColor: "booking.noShow.solid",
  outlineStyle: "solid",
});
